import type {
  GenerationOutputFormat,
  GenerationQuality,
  GenerationReference,
} from "./types.js";

export interface GenerationDraft {
  mode: "client_template" | "free_prompt";
  clientId: string;
  templateId: string;
  platformId: string;
  values: Record<string, string | number | boolean>;
  selected: string[];
  references: GenerationReference[];
  count: number;
  brief: string;
  instruction: string;
  seoQuery: string;
  quality: GenerationQuality;
  format: GenerationOutputFormat;
  transparent: boolean;
  useBrandProfile: boolean;
}

const PREFIX = "image-studio:create-draft:v1:";
const MAX_AGE = 7 * 86400000;
export function readGenerationDraft(userId: string): GenerationDraft | null {
  try {
    const value = JSON.parse(
      localStorage.getItem(PREFIX + userId) ?? "null",
    ) as { savedAt: number; draft: GenerationDraft } | null;
    const d = value?.draft;
    if (
      !value ||
      Date.now() - value.savedAt > MAX_AGE ||
      !Number.isFinite(value.savedAt) ||
      !d ||
      !["client_template", "free_prompt"].includes(d.mode) ||
      ![
        d.clientId,
        d.templateId,
        d.platformId,
        d.brief,
        d.instruction,
        d.seoQuery,
      ].every((v) => typeof v === "string") ||
      d.brief.length > 20000 ||
      d.instruction.length > 10000 ||
      d.seoQuery.length > 240 ||
      !Array.isArray(d.references) ||
      d.references.length > 20 ||
      !d.references.every(
        (r) =>
          r &&
          typeof r.id === "string" &&
          /^[0-9a-f-]{36}$/i.test(r.id) &&
          typeof r.originalFilename === "string",
      ) ||
      !Array.isArray(d.selected) ||
      d.selected.length > 20 ||
      !d.selected.every((id) => typeof id === "string") ||
      !d.values ||
      typeof d.values !== "object" ||
      Array.isArray(d.values) ||
      !Object.values(d.values).every(
        (v) =>
          typeof v === "boolean" ||
          (typeof v === "number" && Number.isFinite(v)) ||
          (typeof v === "string" && v.length <= 15000) ||
          v === null,
      ) ||
      !["draft", "standard", "maximum"].includes(d.quality) ||
      !["png", "jpeg", "webp"].includes(d.format) ||
      !Number.isInteger(d.count) ||
      d.count < 1 ||
      d.count > 20
    ) {
      clearGenerationDraft(userId);
      return null;
    }
    return d;
  } catch {
    return null;
  }
}
export function saveGenerationDraft(userId: string, draft: GenerationDraft) {
  try {
    localStorage.setItem(
      PREFIX + userId,
      JSON.stringify({ savedAt: Date.now(), draft }),
    );
    return true;
  } catch {
    return false;
  }
}
export function clearGenerationDraft(userId: string) {
  try {
    localStorage.removeItem(PREFIX + userId);
  } catch {
    /* Storage may be disabled. */
  }
}
export function clearAllGenerationDrafts() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    /* Storage may be disabled. */
  }
}
